import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types/index.ts';
import { authAPI } from '../services/api.ts';

interface AuthContextType {
  user: User | null;
  token: string | null;
  userRole: 'customer' | 'admin' | null;
  login: (email: string, password: string, showWelcome?: boolean) => Promise<void>;
  adminLogin: (userData: { user: User; token: string }) => void;
  register: (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
  }) => Promise<void>;
  logout: () => void;
  clearOtherSession: (currentRole: 'customer' | 'admin') => void;
  updateProfile: (userData: {
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
  }) => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  isCustomer: boolean;
  isAdmin: boolean;
  showSuccessBanner: boolean;
  setShowSuccessBanner: (show: boolean) => void;
  successMessage: string | null;
  setSuccessMessage: (message: string | null) => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setUserRole: (role: 'customer' | 'admin' | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'customer' | 'admin' | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Helper functions for localStorage management
  const getStorageKeys = (role: 'customer' | 'admin') => ({
    token: `${role}_token`,
    user: `${role}_user`,
    role: `${role}_role`
  });

  const clearOtherSession = (currentRole: 'customer' | 'admin') => {
    const otherRole = currentRole === 'customer' ? 'admin' : 'customer';
    const otherKeys = getStorageKeys(otherRole);
    
    localStorage.removeItem(otherKeys.token);
    localStorage.removeItem(otherKeys.user);
    localStorage.removeItem(otherKeys.role);
    
    console.log(`Cleared ${otherRole} session to prevent conflicts`);
  };

  const saveSession = (userData: User, userToken: string, role: 'customer' | 'admin') => {
    const keys = getStorageKeys(role);
    
    // Clear other session first
    clearOtherSession(role);
    
    // Save current session
    localStorage.setItem(keys.token, userToken);
    localStorage.setItem(keys.user, JSON.stringify(userData));
    localStorage.setItem(keys.role, role);
    
    setUser(userData);
    setToken(userToken);
    setUserRole(role);
  };

  const loadSession = () => {
    // Try to load customer session first
    const customerKeys = getStorageKeys('customer');
    const customerToken = localStorage.getItem(customerKeys.token);
    const customerUser = localStorage.getItem(customerKeys.user);
    const customerRole = localStorage.getItem(customerKeys.role);

    // Try to load admin session
    const adminKeys = getStorageKeys('admin');
    const adminToken = localStorage.getItem(adminKeys.token);
    const adminUser = localStorage.getItem(adminKeys.user);
    const adminRole = localStorage.getItem(adminKeys.role);

    // Determine which session to load (admin takes priority if both exist)
    if (adminToken && adminUser && adminRole === 'admin') {
      setToken(adminToken);
      setUser(JSON.parse(adminUser));
      setUserRole('admin');
      console.log('Loaded admin session');
    } else if (customerToken && customerUser && customerRole === 'customer') {
      setToken(customerToken);
      setUser(JSON.parse(customerUser));
      setUserRole('customer');
      console.log('Loaded customer session');
    } else {
      // Clean up any corrupted session data
      localStorage.removeItem(customerKeys.token);
      localStorage.removeItem(customerKeys.user);
      localStorage.removeItem(customerKeys.role);
      localStorage.removeItem(adminKeys.token);
      localStorage.removeItem(adminKeys.user);
      localStorage.removeItem(adminKeys.role);
      console.log('No valid session found, cleared all session data');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string, showWelcome = false) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, token: userToken } = response.data.data;
      
      // Determine role and save session accordingly
      const role = userData.role === 'admin' ? 'admin' : 'customer';
      saveSession(userData, userToken, role);
      
      // Show success banner if this is a post-registration login
      if (showWelcome) {
        console.log('Post-registration login - showing success banner');
        setSuccessMessage('Account created and verified successfully!');
        setShowSuccessBanner(true);
      }
      
      console.log(`Customer login successful for: ${userData.email}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
    phone: string;
    address?: string;
    city?: string;
    pincode?: string;
  }) => {
    try {
      const response = await authAPI.register(userData);
      const { user: newUser, token: userToken } = response.data.data;
      
      // Registration always creates customer accounts
      saveSession(newUser, userToken, 'customer');
      
      console.log(`Customer registration successful for: ${newUser.email}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const adminLogin = (userData: { user: User; token: string }) => {
    // Admin login always uses admin session
    saveSession(userData.user, userData.token, 'admin');
    
    console.log(`Admin login successful for: ${userData.user.email}`);
  };

  const logout = () => {
    // Clear current session
    if (userRole) {
      const keys = getStorageKeys(userRole);
      localStorage.removeItem(keys.token);
      localStorage.removeItem(keys.user);
      localStorage.removeItem(keys.role);
      console.log(`Logged out from ${userRole} session`);
    }
    
    // Clear state
    setUser(null);
    setToken(null);
    setUserRole(null);
  };

  const updateProfile = async (userData: {
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    pincode?: string;
  }) => {
    try {
      const response = await authAPI.updateProfile(userData);
      const updatedUser = response.data.data.user;
      
      // Update session with new user data
      if (userRole && token) {
        saveSession(updatedUser, token, userRole);
        console.log(`Profile updated for ${userRole}: ${updatedUser.email}`);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Profile update failed');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    userRole,
    login,
    adminLogin,
    register,
    logout,
    clearOtherSession,
    updateProfile,
    loading,
    isAuthenticated: !!user && !!token,
    isCustomer: userRole === 'customer',
    isAdmin: userRole === 'admin',
    showSuccessBanner,
    setShowSuccessBanner,
    successMessage,
    setSuccessMessage,
    setUser,
    setToken,
    setUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
